import React, { useState } from "react";
import {
  Plus,
  Trash2,
  Star,
  Package,
  Palette,
  ImageIcon,
  X,
  Upload,
  Settings,
} from "lucide-react";
import toast from "react-hot-toast";
import BulkVariantUpdateModal from "./BulkVariantUpdateModal";

const VariantManager = ({ variants, onChange }) => {
  const [expandedVariant, setExpandedVariant] = useState(0);
  const [showBulkModal, setShowBulkModal] = useState(false);

  const addVariant = () => {
    const newVariant = {
      id: `new-${Date.now()}`,
      sku: "",
      price_regular: "",
      price_sale: "",
      stock: 0,
      weight_grams: "",
      is_default: variants.length === 0,
      is_active: true,
      attributes: [],
    };
    onChange([...variants, newVariant]);
    setExpandedVariant(variants.length);
  };

  const removeVariant = (index) => {
    if (variants.length <= 1) {
      alert("At least one variant is required");
      return;
    }

    const updatedVariants = variants.filter((_, i) => i !== index);

    // If we removed the default variant, make the first one default
    if (variants[index].is_default && updatedVariants.length > 0) {
      updatedVariants[0].is_default = true;
    }

    onChange(updatedVariants);
    setExpandedVariant(Math.max(0, expandedVariant - 1));
  };

  const updateVariant = (index, field, value) => {
    const updatedVariants = variants.map((variant, i) => {
      if (i === index) {
        // If setting this variant as default, unset others
        if (field === "is_default" && value) {
          const otherVariants = variants.map((v, vi) =>
            vi === index
              ? { ...v, [field]: value }
              : { ...v, is_default: false }
          );
          return otherVariants[index];
        }
        return { ...variant, [field]: value };
      }
      // If setting another variant as default, unset this one
      if (field === "is_default" && value) {
        return { ...variant, is_default: false };
      }
      return variant;
    });
    onChange(updatedVariants);
  };

  const addAttribute = (variantIndex) => {
    const updatedVariants = variants.map((variant, i) => {
      if (i === variantIndex) {
        return {
          ...variant,
          attributes: [
            ...variant.attributes,
            { id: `attr-${Date.now()}`, name: "", value: "" },
          ],
        };
      }
      return variant;
    });
    onChange(updatedVariants);
  };

  const removeAttribute = (variantIndex, attributeIndex) => {
    const updatedVariants = variants.map((variant, i) => {
      if (i === variantIndex) {
        return {
          ...variant,
          attributes: variant.attributes.filter(
            (_, ai) => ai !== attributeIndex
          ),
        };
      }
      return variant;
    });
    onChange(updatedVariants);
  };

  const updateAttribute = (variantIndex, attributeIndex, field, value) => {
    const updatedVariants = variants.map((variant, i) => {
      if (i === variantIndex) {
        return {
          ...variant,
          attributes: variant.attributes.map((attr, ai) =>
            ai === attributeIndex ? { ...attr, [field]: value } : attr
          ),
        };
      }
      return variant;
    });
    onChange(updatedVariants);
  };

  const generateSKU = (variantIndex) => {
    const variant = variants[variantIndex];
    const baseSKU = "PRD";
    const timestamp = Date.now().toString().slice(-6);
    const attributes = variant.attributes
      .filter((attr) => attr.name && attr.value)
      .map((attr) => attr.value.substring(0, 3).toUpperCase())
      .join("-");

    const generatedSKU = attributes
      ? `${baseSKU}-${attributes}-${timestamp}`
      : `${baseSKU}-${timestamp}`;

    updateVariant(variantIndex, "sku", generatedSKU);
  };

  // Image management functions
  const handleAddVariantImages = (variantIndex) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "image/*";

    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      const newImages = [];

      for (const file of files) {
        const preview = URL.createObjectURL(file);
        const newImage = {
          id: `img-${Date.now()}-${Math.random()}`,
          file,
          preview,
          alt_text: `${variants[variantIndex].sku || "Variant"} image`,
          is_primary: false,
          position:
            (variants[variantIndex].images?.length || 0) + newImages.length,
        };
        newImages.push(newImage);
      }

      // If this is the first image, make it primary
      if (
        !variants[variantIndex].images ||
        variants[variantIndex].images.length === 0
      ) {
        newImages[0].is_primary = true;
      }

      const updatedVariants = variants.map((variant, i) => {
        if (i === variantIndex) {
          return {
            ...variant,
            images: [...(variant.images || []), ...newImages],
          };
        }
        return variant;
      });

      onChange(updatedVariants);
    };

    input.click();
  };

  const handleRemoveVariantImage = (variantIndex, imageIndex) => {
    const updatedVariants = variants.map((variant, i) => {
      if (i === variantIndex) {
        const updatedImages = variant.images.filter(
          (_, ii) => ii !== imageIndex
        );

        // If we removed the primary image, make the first remaining image primary
        if (
          variant.images[imageIndex]?.is_primary &&
          updatedImages.length > 0
        ) {
          updatedImages[0].is_primary = true;
        }

        // Clean up object URL if it exists
        if (variant.images[imageIndex]?.preview) {
          URL.revokeObjectURL(variant.images[imageIndex].preview);
        }

        return {
          ...variant,
          images: updatedImages,
        };
      }
      return variant;
    });

    onChange(updatedVariants);
  };

  const handleSetVariantPrimaryImage = (variantIndex, imageIndex) => {
    const updatedVariants = variants.map((variant, i) => {
      if (i === variantIndex) {
        return {
          ...variant,
          images: variant.images.map((img, ii) => ({
            ...img,
            is_primary: ii === imageIndex,
          })),
        };
      }
      return variant;
    });

    onChange(updatedVariants);
  };

  const handleUpdateImageAltText = (variantIndex, imageIndex, altText) => {
    const updatedVariants = variants.map((variant, i) => {
      if (i === variantIndex) {
        return {
          ...variant,
          images: variant.images.map((img, ii) =>
            ii === imageIndex ? { ...img, alt_text: altText } : img
          ),
        };
      }
      return variant;
    });

    onChange(updatedVariants);
  };

  const handleUpdateCustomFilename = (
    variantIndex,
    imageIndex,
    customFilename
  ) => {
    const updatedVariants = variants.map((variant, i) => {
      if (i === variantIndex) {
        return {
          ...variant,
          images: variant.images.map((img, ii) =>
            ii === imageIndex
              ? { ...img, custom_filename: customFilename }
              : img
          ),
        };
      }
      return variant;
    });

    onChange(updatedVariants);
  };

  const handleBulkUpdate = (updateData) => {
    console.log("handleBulkUpdate called with:", updateData);
    console.log("Current variants before update:", variants);

    if (!updateData || Object.keys(updateData).length === 0) {
      console.error("No update data provided");
      toast.error("No changes to apply");
      return;
    }

    // Apply bulk update to all variants while preserving unique fields
    const updatedVariants = variants.map((variant, index) => {
      const updatedVariant = { ...variant };

      // Apply updates only for fields that were provided
      if (updateData.hasOwnProperty("price_regular")) {
        updatedVariant.price_regular = updateData.price_regular;
        console.log(
          `Variant ${index} price_regular updated to:`,
          updatedVariant.price_regular
        );
      }
      if (updateData.hasOwnProperty("price_sale")) {
        updatedVariant.price_sale =
          updateData.price_sale !== null && updateData.price_sale !== ""
            ? updateData.price_sale
            : null;
        console.log(
          `Variant ${index} price_sale updated to:`,
          updatedVariant.price_sale
        );
      }
      if (updateData.hasOwnProperty("stock")) {
        // Ensure stock is a number
        updatedVariant.stock =
          typeof updateData.stock === "number"
            ? updateData.stock
            : parseInt(updateData.stock) || 0;
        console.log(`Variant ${index} stock updated to:`, updatedVariant.stock);
      }
      if (updateData.hasOwnProperty("weight_grams")) {
        updatedVariant.weight_grams =
          updateData.weight_grams !== null && updateData.weight_grams !== ""
            ? updateData.weight_grams
            : null;
        console.log(
          `Variant ${index} weight_grams updated to:`,
          updatedVariant.weight_grams
        );
      }
      if (updateData.hasOwnProperty("is_active")) {
        updatedVariant.is_active = updateData.is_active;
        console.log(
          `Variant ${index} is_active updated to:`,
          updatedVariant.is_active
        );
      }

      // Preserve unique fields: sku, is_default, attributes, images
      // These are already preserved by spreading the variant

      return updatedVariant;
    });

    console.log("Updated variants after bulk update:", updatedVariants);

    // Show success message
    const updatedFields = Object.keys(updateData).join(", ");
    toast.success(
      `Updated ${updatedFields} for all ${variants.length} variant${
        variants.length !== 1 ? "s" : ""
      }`
    );

    // Call onChange to update parent component
    onChange(updatedVariants);
  };

  return (
    <div className="space-y-4">
      {/* Set All Variants Button */}
      {variants.length > 1 && (
        <div className="flex justify-end mb-2">
          <button
            type="button"
            onClick={() => setShowBulkModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <Settings size={16} />
            Set All Variants
          </button>
        </div>
      )}

      {/* Bulk Update Modal */}
      <BulkVariantUpdateModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        onApply={handleBulkUpdate}
        variantCount={variants.length}
      />

      {/* Variants List */}
      {variants.map((variant, index) => (
        <div
          key={variant.id || index}
          className="border border-gray-200 rounded-lg"
        >
          {/* Variant Header */}
          <div
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
            onClick={() =>
              setExpandedVariant(expandedVariant === index ? -1 : index)
            }
          >
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                {variant.is_default && (
                  <Star
                    size={16}
                    className="text-yellow-500"
                    fill="currentColor"
                  />
                )}
                <Package size={16} className="text-gray-500" />
                <span className="font-medium">
                  Variant {index + 1}
                  {variant.sku && ` - ${variant.sku}`}
                </span>
              </div>
              {variant.attributes.length > 0 && (
                <div className="flex gap-1">
                  {variant.attributes.slice(0, 3).map((attr, i) => (
                    <span
                      key={i}
                      className="text-xs bg-gray-100 px-2 py-1 rounded"
                    >
                      {attr.name}: {attr.value}
                    </span>
                  ))}
                  {variant.attributes.length > 3 && (
                    <span className="text-xs text-gray-500">
                      +{variant.attributes.length - 3} more
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">
                ${variant.price_regular} • Stock: {variant.stock}
              </span>
              {variants.length > 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeVariant(index);
                  }}
                  className="text-red-500 hover:text-red-700 p-1"
                  title="Remove variant"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Variant Details */}
          {expandedVariant === index && (
            <div className="border-t border-gray-200 p-4 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SKU *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={variant.sku}
                      onChange={(e) =>
                        updateVariant(index, "sku", e.target.value)
                      }
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => generateSKU(index)}
                      className="px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                      title="Generate SKU"
                    >
                      Gen
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Regular Price * ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={variant.price_regular || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      updateVariant(
                        index,
                        "price_regular",
                        value === "" ? "" : parseFloat(value) || ""
                      );
                    }}
                    placeholder="Enter regular price"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sale Price ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={variant.price_sale || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      updateVariant(
                        index,
                        "price_sale",
                        value === "" ? "" : parseFloat(value) || ""
                      );
                    }}
                    placeholder="Enter sale price (optional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stock Quantity *
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={
                      variant.stock !== undefined && variant.stock !== null
                        ? variant.stock
                        : ""
                    }
                    onChange={(e) => {
                      const value = e.target.value;
                      updateVariant(
                        index,
                        "stock",
                        value === "" ? 0 : parseInt(value) || 0
                      );
                    }}
                    placeholder="Enter stock quantity"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Weight (grams)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={variant.weight_grams || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      updateVariant(
                        index,
                        "weight_grams",
                        value === "" ? "" : value
                      );
                    }}
                    placeholder="Enter weight in grams"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                <div className="flex flex-col justify-end">
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={variant.is_default}
                        onChange={(e) =>
                          updateVariant(index, "is_default", e.target.checked)
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Default variant
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={variant.is_active}
                        onChange={(e) =>
                          updateVariant(index, "is_active", e.target.checked)
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Attributes */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">
                    Variant Attributes
                  </h4>
                  <button
                    type="button"
                    onClick={() => addAttribute(index)}
                    className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                  >
                    <Plus size={14} />
                    Add Attribute
                  </button>
                </div>

                {variant.attributes.length === 0 ? (
                  <p className="text-sm text-gray-500 py-2">
                    No attributes added. Click "Add Attribute" to specify
                    variant properties like color, size, etc.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {variant.attributes.map((attribute, attrIndex) => (
                      <div
                        key={attribute.id || attrIndex}
                        className="flex gap-2"
                      >
                        <input
                          type="text"
                          placeholder="Attribute name (e.g., Color, Size)"
                          value={attribute.name}
                          onChange={(e) =>
                            updateAttribute(
                              index,
                              attrIndex,
                              "name",
                              e.target.value
                            )
                          }
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Value (e.g., Red, Large)"
                          value={attribute.value}
                          onChange={(e) =>
                            updateAttribute(
                              index,
                              attrIndex,
                              "value",
                              e.target.value
                            )
                          }
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => removeAttribute(index, attrIndex)}
                          className="text-red-500 hover:text-red-700 p-2"
                          title="Remove attribute"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Attribute Suggestions */}
              <div className="bg-gray-50 rounded-md p-3">
                <h5 className="text-xs font-medium text-gray-700 mb-2">
                  Common Attributes:
                </h5>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "Color", icon: Palette },
                    { name: "Size", icon: Package },
                    { name: "Material", icon: Package },
                    { name: "Weight", icon: Package },
                    { name: "Length", icon: Package },
                  ].map(({ name, icon: Icon }) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => {
                        const hasAttribute = variant.attributes.some(
                          (attr) =>
                            attr.name.toLowerCase() === name.toLowerCase()
                        );
                        if (!hasAttribute) {
                          const updatedVariants = variants.map((v, i) => {
                            if (i === index) {
                              return {
                                ...v,
                                attributes: [
                                  ...v.attributes,
                                  { id: `attr-${Date.now()}`, name, value: "" },
                                ],
                              };
                            }
                            return v;
                          });
                          onChange(updatedVariants);
                        }
                      }}
                      className="flex items-center gap-1 px-2 py-1 text-xs bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors"
                    >
                      <Icon size={12} />
                      {name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Variant Images */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700">
                    Variant Images
                  </h4>
                  <button
                    type="button"
                    onClick={() => handleAddVariantImages(index)}
                    className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1 font-medium"
                  >
                    <Upload size={14} />
                    Add Images
                  </button>
                </div>

                {/* Images Display */}
                {variant.images && variant.images.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {variant.images.map((image, imgIndex) => (
                      <div key={image.id || imgIndex} className="space-y-2">
                        {/* Image Container with Controls */}
                        <div className="relative group">
                          <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                            <img
                              src={image.preview || image.url}
                              alt={
                                image.alt_text ||
                                `Variant image ${imgIndex + 1}`
                              }
                              className="w-full h-full object-cover"
                            />
                          </div>

                          {/* Image Controls Overlay - Only covers the image area */}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto">
                            <div className="flex space-x-2">
                              <button
                                type="button"
                                onClick={() =>
                                  handleSetVariantPrimaryImage(index, imgIndex)
                                }
                                className={`p-2 rounded-full ${
                                  image.is_primary
                                    ? "bg-yellow-500 text-white"
                                    : "bg-white text-gray-700 hover:bg-gray-100"
                                }`}
                                title={
                                  image.is_primary
                                    ? "Primary image"
                                    : "Set as primary"
                                }
                              >
                                <Star
                                  size={14}
                                  fill={
                                    image.is_primary ? "currentColor" : "none"
                                  }
                                />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveVariantImage(index, imgIndex)
                                }
                                className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                                title="Remove image"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </div>

                          {/* Primary Badge */}
                          {image.is_primary && (
                            <div className="absolute top-2 left-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded-full font-medium z-10">
                              Primary
                            </div>
                          )}
                        </div>

                        {/* Input Fields - Separated from image container */}
                        <div className="space-y-1">
                          <input
                            type="text"
                            placeholder="Alt text for SEO"
                            value={image.alt_text || ""}
                            onChange={(e) =>
                              handleUpdateImageAltText(
                                index,
                                imgIndex,
                                e.target.value
                              )
                            }
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                          <input
                            type="text"
                            placeholder="Custom filename (optional)"
                            value={image.custom_filename || ""}
                            onChange={(e) =>
                              handleUpdateCustomFilename(
                                index,
                                imgIndex,
                                e.target.value
                              )
                            }
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <ImageIcon className="mx-auto h-8 w-8 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">
                      No images for this variant
                    </p>
                    <p className="text-xs text-gray-400 mb-3">
                      Add specific images for different variant attributes
                      (colors, angles, etc.)
                    </p>
                    <button
                      type="button"
                      onClick={() => handleAddVariantImages(index)}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                    >
                      <Upload size={16} />
                      Upload Images
                    </button>
                  </div>
                )}

                {/* Image Tips */}
                {variant.images && variant.images.length > 0 && (
                  <div className="bg-amber-50 rounded-md p-3">
                    <p className="text-xs text-amber-700">
                      <strong>Tip:</strong> Use high-quality images that
                      showcase this specific variant. The primary image will be
                      shown first to customers.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Add Variant Button */}
      <button
        type="button"
        onClick={addVariant}
        className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center gap-2"
      >
        <Plus size={16} />
        Add New Variant
      </button>

      {/* Variant Tips */}
      <div className="bg-blue-50 rounded-lg p-3">
        <h4 className="text-sm font-medium text-blue-700 mb-2">
          Variant Tips:
        </h4>
        <ul className="text-xs text-blue-600 space-y-1">
          <li>• Each product must have at least one variant</li>
          <li>
            • One variant must be marked as default (shown first to customers)
          </li>
          <li>
            • Use attributes to specify variant differences (color, size,
            material, etc.)
          </li>
          <li>• SKUs must be unique across all products</li>
          <li>• Set sale price lower than regular price for discounts</li>
        </ul>
      </div>
    </div>
  );
};

export default VariantManager;
